import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ENTITIES, getEntityByKey } from '../../constants';
import { useEntityList } from '../../hooks/useEntityList';
import { filterItems } from '../../utils/filterUtils';
import EntityTable from './EntityTable';
import EntityForm from './EntityForm';
import EntityEditForm from './EntityEditForm';
import ConfirmDialog from './ConfirmDialog';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import sprite from '../../images/icons.svg';

const EntityManager = ({
  entityKey,
  selectedDate = new Date(),
  userEmail = '',
  getOptionLabel,
  memberTypeNames = [],
  onUserAdded
}) => {
  const { t } = useTranslation();
  const entity = getEntityByKey(entityKey) || ENTITIES[entityKey];

  const [fields, setFields] = useState();
  const [showForm, setShowForm] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { items, isLoading, error, reload, addItem, updateItem, removeItem } = useEntityList(
    entity,
    selectedDate,
    userEmail
  );

  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery('');
    setShowForm(null);
    setFields(null);
    setFormError(null);
    setSuccessMessage(null);
    setItemToDelete(null);
  }, [entityKey]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const filteredItems = useMemo(() => {
    return filterItems(items, searchQuery, entity?.columns, getOptionLabel, memberTypeNames);
  }, [items, searchQuery, entity?.columns, getOptionLabel, memberTypeNames]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const validPage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedItems = useMemo(() => {
    const startIndex = (validPage - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  }, [filteredItems, validPage, pageSize]);

  const handleEditItem = (item) => {
    setFields(item);
    setFormError(null);
    setShowForm('edit');
  };

  const handleAddItem = async (itemValues) => {
    try {
      setFormError(null);
      setSuccessMessage(null);
      const dataToAdd = itemValues || fields;
      await addItem(dataToAdd);
      setFields({});
      setShowForm(null);
      setSuccessMessage(t('item-added-successfully') || 'Item added successfully');
      if (onUserAdded && entityKey === 'users') {
        onUserAdded(dataToAdd);
      }
    } catch (err) {
      console.error('Error adding item:', err);
      setFormError(err.message || 'Failed to add item');
    }
  };

  const handleDelete = async () => {
    try {
      await removeItem(itemToDelete);
    } catch (err) {
      console.error('Error deleting item:', err);
    }
    setItemToDelete(null);
  };

  const toggleForm = (type) => {
    setFields({});
    setFormError(null);
    setShowForm(prev => (prev === type ? null : type));
  };

  if (!entity) return null;

  const displayError = formError || error;

  const noResultsText = (!t('no-search-results') || t('no-search-results') === 'no-search-results')
    ? 'Inga resultat matchade sökningen.'
    : t('no-search-results');

  return (
    <>
      {successMessage && (
        <div
          className="banner-card success-banner card wrapper-width flex flex-align-center-v"
          style={{
            background: '#e8f5e9',
            color: '#2e7d32',
            padding: '10px 20px',
            margin: '10px auto',
            borderRadius: '4px',
            justifyContent: 'space-between'
          }}
        >
          <span>{successMessage}</span>
          <button
            className="button"
            style={{ padding: '4px 10px', background: '#2e7d32', color: '#fff' }}
            onClick={() => setSuccessMessage(null)}
          >
            ✕
          </button>
        </div>
      )}

      {displayError && (
        <div className="banner-card error-banner card wrapper-width flex flex-align-center-v" style={{ background: '#ffebee', color: '#c62828', padding: '10px 20px', margin: '10px auto', borderRadius: '4px', justifyContent: 'space-between' }}>
          <span>{displayError}</span>
          <button className="button" style={{ padding: '4px 10px' }} onClick={() => { setFormError(null); reload(); }}>Retry</button>
        </div>
      )}

      <div className="flex center wrapper-width-small">

        <div className="card card-large padding-card-small width-max-content position-relative">

          <div className="flex flex-align-between flex-align-center-h" style={{paddingBottom:'11px'}}>

            <SearchBar
                value={searchQuery}
                onChange={(val) => {
                  setSearchQuery(val);
                  setCurrentPage(1);
                }}
                placeholder={t('search')}
            />

            {entity.canAdd && (
                <svg
                    className="new-item"
                    onClick={() => toggleForm('add')}
                    width="31"
                    height="27"
                    style={{ cursor: 'pointer' }}
                >
                  <use href={sprite + "#svg-plus"} />
                </svg>
            )}

          </div>

          <ul id="items-list" className={`${entity.listAction}`}>
            {isLoading ? (
              <div className="spinner"></div>
            ) : (
              <>
                <EntityTable
                  items={paginatedItems}
                  columns={entity.columns}
                  schema={entity.schema}
                  listType={entity.listAction}
                  getOptionLabel={getOptionLabel}
                  onEdit={entity.canAdd ? handleEditItem : undefined}
                  onDelete={entity.canDelete ? setItemToDelete : undefined}
                  canEdit={entity.canAdd}
                  canDelete={entity.canDelete}
                  allMemberTypeNames={memberTypeNames}
                  emptyMessage={searchQuery ? noResultsText : undefined}
                />


              </>
            )}

            <ConfirmDialog
              isOpen={Boolean(itemToDelete)}
              title={t('confirm-edit')}
              message={t('confirm-delete')}
              onConfirm={handleDelete}
              onCancel={() => setItemToDelete(null)}
            />
          </ul>

          {filteredItems.length > 0 && (
              <Pagination
                  currentPage={validPage}
                  totalItems={filteredItems.length}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setCurrentPage(1);
                  }}
                  pageSizeOptions={[25, 50, 100]}
              />
          )}

        </div>

        <div id="items-list-edit" className={`width-max-content card card-large padding-card-small ${showForm ? 'fade-in' : 'fade-out'}`} style={{ marginLeft: '20px' }}>
          {showForm === 'edit' && fields && (
            <div key={`edit-${fields?.id || fields?.email || 'edit'}`} className=" fade-in">
              <EntityEditForm
                fields={fields}
                schema={entity.schema}
                onSaveField={async (field, val) => {
                  const conditionKey = entity.conditionKey || (fields.email ? 'email' : Object.keys(entity.schema)[0]);
                  const conditionVal = fields[conditionKey];
                  await updateItem(conditionVal, field, val);
                  setFields(prev => ({ ...prev, [field]: val }));
                }}
                getOptionLabel={getOptionLabel}
                listType={entity.listAction}
                selectedDate={selectedDate}
                memberTypeNames={memberTypeNames}
              />
            </div>
          )}

          {showForm === 'add' && (
            <div key="add" className="fade-in">
              <EntityForm
                schema={entity.schema}
                values={fields || {}}
                onChange={(key, val) => setFields(prev => ({ ...prev, [key]: val }))}
                onSubmit={handleAddItem}
                getOptionLabel={getOptionLabel}
                memberTypeNames={memberTypeNames}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EntityManager;
export { EntityManager };
